const QUALITY_PAGE_VIEW_EVENT_NAME = 'QualityPageView';
const QUALITY_VISITOR_EVENT_NAME = 'QualityVisitor';
const QUALITY_VISITOR_SESSION_KEY = 'meta-quality-visitor-tracked';
const QUALITY_TRAFFIC_DELAY_MS = 20000;
const HISTORY_CHANGE_EVENT = 'aow:history-change';

let started = false;
let timeoutId: number | null = null;
let currentPagePath = '';
let remainingVisibleMs = QUALITY_TRAFFIC_DELAY_MS;
let visibleStartedAt: number | null = null;

function clearQualityTrafficTimer(): void {
  if (timeoutId !== null) {
    window.clearTimeout(timeoutId);
    timeoutId = null;
  }
}

function hasTrackedQualityVisitor(): boolean {
  try {
    return window.sessionStorage.getItem(QUALITY_VISITOR_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markQualityVisitorTracked(): void {
  try {
    window.sessionStorage.setItem(QUALITY_VISITOR_SESSION_KEY, '1');
  } catch {
    // Storage can be unavailable in some browser privacy modes.
  }
}

function trackQualityTraffic(pagePath: string): void {
  const metaPixel = window.fbq as facebook.Pixel.Event | undefined;

  if (typeof metaPixel !== 'function') {
    return;
  }

  const payload = {
    page_path: pagePath,
    page_title: document.title,
    threshold_seconds: QUALITY_TRAFFIC_DELAY_MS / 1000,
    locale: window.LOCALE
  };

  metaPixel('trackCustom', QUALITY_PAGE_VIEW_EVENT_NAME, payload);

  if (!hasTrackedQualityVisitor()) {
    metaPixel('trackCustom', QUALITY_VISITOR_EVENT_NAME, payload);
    markQualityVisitorTracked();
  }
}

function scheduleQualityTrafficTimer(): void {
  clearQualityTrafficTimer();

  if (document.visibilityState === 'hidden' || remainingVisibleMs <= 0) {
    return;
  }

  visibleStartedAt = Date.now();
  timeoutId = window.setTimeout(() => {
    timeoutId = null;
    visibleStartedAt = null;
    remainingVisibleMs = 0;

    if (window.location.pathname === currentPagePath) {
      trackQualityTraffic(currentPagePath);
    }
  }, remainingVisibleMs);
}

function pauseQualityTrafficTimer(): void {
  if (visibleStartedAt !== null) {
    remainingVisibleMs = Math.max(0, remainingVisibleMs - (Date.now() - visibleStartedAt));
    visibleStartedAt = null;
  }

  clearQualityTrafficTimer();
}

function startPageQualityTrafficTimer(): void {
  const pagePath = window.location.pathname;

  if (pagePath === currentPagePath && timeoutId !== null) {
    return;
  }

  pauseQualityTrafficTimer();
  currentPagePath = pagePath;
  remainingVisibleMs = QUALITY_TRAFFIC_DELAY_MS;
  scheduleQualityTrafficTimer();
}

function dispatchHistoryChangeIfPageChanged(previousPagePath: string): void {
  if (window.location.pathname !== previousPagePath) {
    window.dispatchEvent(new Event(HISTORY_CHANGE_EVENT));
  }
}

function wrapHistoryMethod(method: 'pushState' | 'replaceState'): void {
  const original = window.history[method];

  window.history[method] = function(
    this: History,
    ...args: Parameters<History[typeof method]>
  ): void {
    const previousPagePath = window.location.pathname;
    original.apply(this, args);
    dispatchHistoryChangeIfPageChanged(previousPagePath);
  } as History[typeof method];
}

export function startMetaQualityTrafficTracking(): void {
  if (started) {
    return;
  }

  started = true;
  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');

  window.addEventListener('popstate', startPageQualityTrafficTimer);
  window.addEventListener(HISTORY_CHANGE_EVENT, startPageQualityTrafficTimer);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') {
      scheduleQualityTrafficTimer();
      return;
    }

    pauseQualityTrafficTimer();
  });

  startPageQualityTrafficTimer();
}
